'use client';

import { useEffect, useId, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  cornerRadiusCopy,
  maxCornerRadiusMm,
  MIN_CORNER_RADIUS_MM,
  normalizeCornerRadiusMm,
  validateCornerRadiusMm,
} from '@/lib/product-options/corner-radius';

type Props = {
  value: unknown;
  sizeMm: { widthMm: number; heightMm: number } | null;
  locale: string;
  onCommit: (radiusMm: number) => void;
};

/**
 * Free corner radius for the "Afgeronde hoeken" shape preset. The value is
 * committed on blur or Enter (not on every keystroke), so the price is not
 * re-checked for each digit typed.
 */
export function CornerRadiusField({ value, sizeMm, locale, onCommit }: Props) {
  const id = useId();
  const copy = cornerRadiusCopy(locale);
  const committed = normalizeCornerRadiusMm(value, sizeMm);
  const max = maxCornerRadiusMm(sizeMm);
  const [draft, setDraft] = useState(String(committed));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(String(committed));
    setError(null);
  }, [committed]);

  const commit = () => {
    const result = validateCornerRadiusMm(draft, sizeMm);
    if (!result.ok) {
      setError(copy.errorRange(result.min, result.max));
      return;
    }
    setError(null);
    if (result.value !== Number(value)) onCommit(result.value);
  };

  return (
    <div className="mt-4 rounded-lg border border-blue-600 bg-blue-50 p-3 sm:p-4">
      <Label htmlFor={id} className="mb-1 block text-xs text-gray-700">{copy.label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={MIN_CORNER_RADIUS_MM}
        max={max}
        step="0.5"
        value={draft}
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-help`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); commit(); } }}
        className="w-32"
      />
      <p id={`${id}-help`} className={`mt-1 text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
        {error || `${copy.help} (${MIN_CORNER_RADIUS_MM} - ${max} mm)`}
      </p>
    </div>
  );
}
